/**
 * AuthProvider — Weiche zwischen den beiden Auth-Modi (VITE_AUTH_MODE).
 *
 *  - "dev": kein Logto-Code wird geladen (lazy import), die Dev-Identitaet
 *    steht sofort im Store (siehe store/auth.ts) — Kinder direkt rendern.
 *  - "authentik": laedt AuthentikAuth (oidc-client-ts gegen auth.rmnc.app).
 *  - "logto": laedt LogtoAuth (LogtoProvider + Login-Gate + /callback) nach.
 *
 * Muss INNERHALB des BrowserRouter haengen (nutzt useLocation/useNavigate).
 */

import { lazy, Suspense, type ReactNode } from "react"
import { AUTH_MODE } from "@/store/auth"

const LogtoAuth = lazy(() => import("./LogtoAuth"))
const AuthentikAuth = lazy(() => import("./AuthentikAuth"))

/** Vollflaechige Ladeanzeige waehrend Login/Redirect (deutsch, ohne Layout) */
export function AuthLoading({ text = "Anmeldung wird geprüft…" }: { text?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-sm">{text}</span>
      </div>
    </div>
  )
}

/** Fehlende Build-Konfiguration klar benennen statt kryptisch fehlzuschlagen */
export function ConfigError({ fehlend }: { fehlend: string[] }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-md space-y-2 text-center">
        <h1 className="text-lg font-semibold">Anmeldung nicht konfiguriert</h1>
        <p className="text-sm text-muted-foreground">
          Der Build wurde ohne vollständige Anmelde-Konfiguration erstellt. Es fehlen:{" "}
          <code>{fehlend.join(", ")}</code>. Bitte an die Administration wenden.
        </p>
      </div>
    </div>
  )
}

/** Verstaendliche deutsche Fehlerseite (z. B. abgebrochener Login) */
export function AuthError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-lg font-semibold">Anmeldung fehlgeschlagen</h1>
        <p className="text-sm text-muted-foreground">
          Die Anmeldung wurde abgebrochen oder konnte nicht abgeschlossen werden. Bitte
          versuchen Sie es erneut — bei wiederholten Problemen an die Administration wenden.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Erneut anmelden
        </button>
      </div>
    </div>
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "dev") {
    return <>{children}</>
  }
  if (AUTH_MODE === "authentik") {
    return (
      <Suspense fallback={<AuthLoading />}>
        <AuthentikAuth>{children}</AuthentikAuth>
      </Suspense>
    )
  }
  return (
    <Suspense fallback={<AuthLoading />}>
      <LogtoAuth>{children}</LogtoAuth>
    </Suspense>
  )
}
